import { CreditAssessment } from '../../../services/schemas/loanServiceSchemas';
import { DirectorReportView } from './creditDirectorReportsTypes';

type ExtendedCreditAssessment = CreditAssessment & {
    directorReports?: unknown;
};

const toRecord = (value: unknown): Record<string, unknown> | null => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return null;
    }
    return value as Record<string, unknown>;
};

const toStringValue = (value: unknown): string | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};

const joinNameParts = (...parts: unknown[]): string | undefined => {
    const values = parts
        .map((part) => toStringValue(part))
        .filter((part): part is string => Boolean(part));

    if (values.length === 0) {
        return undefined;
    }

    return values.join(' ').replace(/\s+/g, ' ').trim();
};

const toNumberValue = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
};

const resolveDirectorName = (item: Record<string, unknown>, xdsReport: Record<string, unknown> | undefined, fallbackIndex: number): string => {
    const consumerDetail = toRecord(xdsReport?.consumerDetail);
    const xdsName = joinNameParts(
        consumerDetail?.firstName,
        consumerDetail?.secondName,
        consumerDetail?.thirdName,
        consumerDetail?.surname
    );

    const attributes = toRecord(item.attributes);

    const attributesName =
        toStringValue(attributes?.directorName) ||
        toStringValue(attributes?.fullName) ||
        toStringValue(attributes?.name) ||
        joinNameParts(
            attributes?.firstName,
            attributes?.middleName,
            attributes?.secondName,
            attributes?.thirdName,
            attributes?.surname || attributes?.lastName || attributes?.familyName
        );

    const directName =
        xdsName ||
        toStringValue(item.directorName) ||
        toStringValue(item.fullName) ||
        toStringValue(item.name) ||
        attributesName;

    if (directName) {
        return directName;
    }

    return xdsName || `Director ${fallbackIndex + 1}`;
};

const mapDirectorReportItem = (
    item: unknown,
    index: number
): DirectorReportView => {
    const record = toRecord(item);
    if (!record) {
        return {
            id: `director-${index + 1}`,
            label: `Director ${index + 1}`,
            status: 'UNKNOWN',
            failureReason: 'Invalid director report payload.'
        };
    }

    const possibleXdsReport =
        toRecord(record.xdsReport) ||
        toRecord(record.report) ||
        (record.enquiryId || record.consumerDetail || record.consumerScoring ? record : undefined);

    const possibleScoringResult =
        toRecord(record.scoringEngineResult) ||
        toRecord(record.engineResult) ||
        undefined;

    const possibleAttributes =
        toRecord(record.attributes) ||
        undefined;

    const failureReason =
        toStringValue(record.failureReason) ||
        toStringValue(record.error) ||
        toStringValue(record.message);

    const statusCandidate = (toStringValue(record.status) || '').toUpperCase();
    let status: DirectorReportView['status'] = 'UNKNOWN';
    if (statusCandidate === 'SUCCESS' || statusCandidate === 'FAILED' || statusCandidate === 'PARTIAL') {
        status = statusCandidate;
    } else if (failureReason) {
        status = 'FAILED';
    } else if (possibleXdsReport || possibleScoringResult) {
        status = 'SUCCESS';
    }

    const score = toNumberValue(toRecord(possibleXdsReport?.consumerScoring)?.finalScore);
    const label = resolveDirectorName(record, possibleXdsReport, index);

    return {
        id:
            toStringValue(record.directorIdNumber) ||
            toStringValue(record.directorId) ||
            toStringValue(record.idNumber) ||
            toStringValue(record.enquiryId) ||
            `director-${index + 1}`,
        label,
        status,
        failureReason,
        creditScore: score,
        attributes: possibleAttributes,
        xdsReport: possibleXdsReport,
        scoringEngineResult: possibleScoringResult
    };
};

export const buildDirectorReports = (assessment?: CreditAssessment): DirectorReportView[] => {
    if (!assessment) {
        return [];
    }

    const extended = assessment as ExtendedCreditAssessment;

    const reportListSource =
        (Array.isArray(assessment.directorResults) ? assessment.directorResults : undefined) ||
        (Array.isArray(extended.directorReports) ? extended.directorReports : undefined);

    if (reportListSource && reportListSource.length > 0) {
        const reports = reportListSource.map((item, index) => mapDirectorReportItem(item, index));
        const labelCount = new Map<string, number>();

        reports.forEach((report) => {
            const currentCount = labelCount.get(report.label) || 0;
            labelCount.set(report.label, currentCount + 1);
        });

        return reports.map((report, index) => {
            if ((labelCount.get(report.label) || 0) <= 1) {
                return report;
            }

            const idSuffix = report.id.startsWith('director-') ? `${index + 1}` : report.id.slice(-6);
            return {
                ...report,
                label: `${report.label} (${idSuffix})`
            };
        });
    }

    return [];
};
